import { useState, useEffect, useMemo } from 'react';
import { financialYearApi } from '@/api/financialYear.api';
import { financialPeriodApi } from '@/api/financialPeriod.api';
import { cn } from '@/utils/cn';
import type { FinancialYearListDto } from '@/types/financialYear.types';
import type { FinancialPeriodListDto } from '@/types/financialPeriod.types';

interface FinancialPeriodSelectProps {
  /** Called with (fromDate, toDate) in YYYY-MM-DD format when a period is selected */
  onPeriodChange: (fromDate: string, toDate: string) => void;
  className?: string;
}

export const FinancialPeriodSelect = ({ onPeriodChange, className }: FinancialPeriodSelectProps) => {
  const [years, setYears] = useState<FinancialYearListDto[]>([]);
  const [selectedYear, setSelectedYear] = useState<FinancialYearListDto | null>(null);
  const [allPeriods, setAllPeriods] = useState<FinancialPeriodListDto[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');

  // Load all financial years on mount
  useEffect(() => {
    financialYearApi.getList({ pageSize: 50 }).then(res => {
      const items: FinancialYearListDto[] = res.data?.data?.items ?? [];
      setYears(items);
      // Auto-select the current year
      const current = items.find(y => y.isCurrent);
      if (current) setSelectedYear(current);
    }).catch(() => {});
  }, []);

  // Load all periods once; filter client-side by fiscal year
  useEffect(() => {
    financialPeriodApi.getList({ pageSize: 200 }).then(res => {
      setAllPeriods(res.data?.data?.items ?? []);
    }).catch(() => { setAllPeriods([]); });
  }, []);

  // Filter periods that belong to the selected financial year.
  // FinancialPeriodListDto.fiscalYear (number) is matched against the numeric
  // value of FinancialYearListDto.yearCode (e.g. yearCode "2024" → 2024).
  const periods = useMemo<FinancialPeriodListDto[]>(() => {
    if (!selectedYear) return [];
    const fiscalYearNum = parseInt(selectedYear.yearCode, 10);
    if (isNaN(fiscalYearNum)) return [];
    return allPeriods
      .filter(p => p.fiscalYear === fiscalYearNum)
      .sort((a, b) => a.periodNumber - b.periodNumber);
  }, [selectedYear, allPeriods]);

  // Reset period selection when year changes
  useEffect(() => {
    setSelectedPeriodId('');
  }, [selectedYear]);

  const handleYearChange = (yearId: string) => {
    const year = years.find(y => y.id === yearId) ?? null;
    setSelectedYear(year);
    setSelectedPeriodId('');
  };

  const handlePeriodSelect = (periodId: string) => {
    setSelectedPeriodId(periodId);
    const period = periods.find(p => p.id === periodId);
    if (period) onPeriodChange(period.startDate, period.endDate);
  };

  const selectCls =
    'flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm ' +
    'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ' +
    'focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <div className={cn('flex gap-2', className)}>
      <select
        value={selectedYear?.id ?? ''}
        onChange={e => handleYearChange(e.target.value)}
        className={selectCls}
      >
        <option value="">— Financial Year —</option>
        {years.map(y => (
          <option key={y.id} value={y.id}>
            {y.yearCode}{y.isCurrent ? ' (Current)' : ''}
          </option>
        ))}
      </select>

      {selectedYear && (
        <select
          value={selectedPeriodId}
          onChange={e => handlePeriodSelect(e.target.value)}
          className={selectCls}
        >
          <option value="">— Select Period —</option>
          {periods.map(p => (
            <option key={p.id} value={p.id}>
              {p.periodCode} — {p.periodName}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};
